/**
 * PreviewPane — the white report card on the right of /reports/new.
 *
 * Layout: the card header carries the title/subtitle on the left and a
 * SEGMENTED TAB SWITCH (Preview · SQL · Template) on the right; the
 * selected panel fills the card body directly — no second tabs row.
 *
 *   - Preview    — the rendered report: server-rendered HTML in a
 *                  sandboxed iframe when the draft carries a custom
 *                  displayTemplate (KPI cards…), else the flat table.
 *   - SQL        — the multi-step data-source stepper. Each block is
 *                  one SQL query (Monaco); add / reorder / delete
 *                  builds a `PipelineStep[]` that the save mutation
 *                  writes to `report_definitions.data_sources`. First
 *                  step (`key: "rows"`) is where the AI's SQL lives.
 *                  Editable once saved.
 *   - Template   — full-height code editor for the Nunjucks HTML.
 *                  Editable once saved (server smoke-renders first).
 *
 * While a propose_report call is streaming, the `pending` prop swaps
 * the whole card for a building state (streamed title + shimmer rows).
 *
 * Historical note: this pane used to host a single `SqlPanel` (one
 * Monaco) and there was a parallel `/reports/build` route with the
 * multi-step builder. That route was retired and its stepper folded
 * in here — one door for chat and manual authoring.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableProperties } from "lucide-react";
import {
  cn,
  TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@flint/ui";
import type { reports as reportsContract } from "@flint/api-contract";
import { ReportFrame } from "../../components/report-frame.tsx";
import { trpc } from "../../lib/trpc.ts";
import { MonacoCodeEditor } from "./monaco-code-editor.tsx";
import { DataSourcesEditor, type DataSource } from "./data-sources-editor.tsx";

type DraftDefinition = reportsContract.DraftDefinition;
type SaveMutation = ReturnType<typeof trpc.reports.saveAiDraft.useMutation>;
type UpdateMutation = ReturnType<typeof trpc.reports.updateAiDraft.useMutation>;
type UpdateTemplateMutation = ReturnType<
  typeof trpc.reports.updateAiDraftTemplate.useMutation
>;

type PreviewTab = "preview" | "sql" | "template";

/**
 * Partial draft extracted from a `propose_report` tool call while its
 * input is still streaming — lets the panel visibly build (title,
 * columns, shimmer rows) during the longest phase of a turn instead
 * of sitting frozen until the SQL has run.
 */
export interface PendingDraft {
  title?: string;
  subtitle?: string;
  columns?: string[];
}

interface PreviewPaneProps {
  draft: DraftDefinition | null;
  /** Set while a propose_report tool call is streaming its input. */
  pending?: PendingDraft | null;
  /** Server-rendered HTML when the draft has a custom displayTemplate. */
  html?: string | null;
  /** URL slug of the saved report — enables the SQL/Template editors. */
  savedKey?: string;
  /**
   * True when the chat was opened via `/reports/new?edit=<key>` — as
   * opposed to `savedKey` becoming truthy because the user just saved
   * in this session. Drives the "Editing existing report" badge +
   * default tab.
   */
  editMode?: boolean;
  /** First-save mutation — footer "Save changes" on an unsaved draft. */
  saveMutation?: SaveMutation;
  /** Mutation for the SQL editor's "Save changes". */
  updateMutation?: UpdateMutation;
  /** Mutation for the Template editor's "Save template". */
  updateTemplateMutation?: UpdateTemplateMutation;
}

export function PreviewPane({
  draft,
  pending,
  html,
  savedKey,
  editMode,
  saveMutation,
  updateMutation,
  updateTemplateMutation,
}: PreviewPaneProps) {
  // Edit-mode (came here via `?edit=<key>`) defaults to the SQL tab —
  // the user is here to modify, not look at empty cells.
  const [tab, setTab] = useState<PreviewTab>(editMode ? "sql" : "preview");

  // Data-source stepper state — the SQL tab's editable pipeline. Seeded
  // from `draft.dataSources` when the row was hydrated in edit-mode;
  // otherwise a single implicit step derived from `sqlTemplate` (the
  // shape a fresh AI draft arrives in). `savedSteps` mirrors what's on
  // the server so the footer's dirty/discard logic works the same way
  // as the pre-stepper single-Monaco version did for `sqlTemplate`.
  const derivedSteps = useMemo<DataSource[]>(
    () => deriveSteps(draft),
    [draft],
  );
  const [editSteps, setEditSteps] = useState<DataSource[]>(derivedSteps);
  const [savedSteps, setSavedSteps] = useState<DataSource[]>(derivedSteps);
  useEffect(() => {
    setEditSteps(derivedSteps);
    setSavedSteps(derivedSteps);
  }, [derivedSteps]);
  const stepsDirty = !stepsEqual(editSteps, savedSteps);
  const lastSqlSuccessRef = useRef<unknown>(null);
  const sqlSuccessData = updateMutation?.data ?? null;
  useEffect(() => {
    if (!sqlSuccessData) return;
    if (lastSqlSuccessRef.current === sqlSuccessData) return;
    lastSqlSuccessRef.current = sqlSuccessData;
    setSavedSteps(editSteps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sqlSuccessData]);

  const patchStep = useCallback((index: number, patch: Partial<DataSource>) => {
    setEditSteps((current) => {
      const next = [...current];
      const existing = next[index];
      if (!existing) return current;
      next[index] = { ...existing, ...patch };
      return next;
    });
  }, []);
  const moveStep = useCallback((index: number, direction: -1 | 1) => {
    setEditSteps((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const a = next[index]!;
      const b = next[target]!;
      next[index] = b;
      next[target] = a;
      return next.map((s, i) => ({ ...s, order: i }));
    });
  }, []);
  const removeStep = useCallback((index: number) => {
    setEditSteps((current) =>
      current.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i })),
    );
  }, []);
  const addStep = useCallback(() => {
    setEditSteps((current) => {
      const nextIndex = current.length;
      const step: DataSource = {
        key: nextIndex === 0 ? "rows" : `step${nextIndex + 1}`,
        name: nextIndex === 0 ? "Rows" : "New data source",
        type: "sql",
        order: nextIndex,
        script: "",
      };
      return [...current, step];
    });
  }, []);

  // Template edit state — hydrated by TemplatePanel's data queries via
  // `onTemplateLoaded`. We keep it here so Save + Discard see it too.
  const [editTpl, setEditTpl] = useState<string>("");
  const [savedTpl, setSavedTpl] = useState<string>("");
  const tplDirty = editTpl !== savedTpl;
  const lastTplSuccessRef = useRef<unknown>(null);
  const tplSuccessData = updateTemplateMutation?.data ?? null;
  useEffect(() => {
    if (!tplSuccessData) return;
    if (lastTplSuccessRef.current === tplSuccessData) return;
    lastTplSuccessRef.current = tplSuccessData;
    setSavedTpl(editTpl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplSuccessData]);

  const handleTemplateLoaded = (source: string) => {
    setEditTpl(source);
    setSavedTpl(source);
  };

  // A propose_report call is streaming — show the report building live.
  if (pending) {
    return (
      <PaneFrame>
        <article className={cardClass}>
          <header className={headerClass}>
            <CardTitle
              title={pending.title ?? "Drafting your report…"}
              subtitle={pending.subtitle}
              badge="Generating"
              badgeTone="progress"
            />
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            <BuildingBody columns={pending.columns ?? []} />
          </div>
          <IdlePreviewFooter />
        </article>
      </PaneFrame>
    );
  }

  if (!draft) {
    return (
      <PaneFrame>
        <article className={cardClass}>
          <header className={headerClass}>
            <div className="flex items-baseline gap-2 text-sm">
              <span className="font-semibold text-ink">Live preview</span>
              <span className="text-line-2">·</span>
              <span className="text-muted">Waiting for a prompt</span>
            </div>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            <EmptyBody />
          </div>
          <IdlePreviewFooter />
        </article>
      </PaneFrame>
    );
  }

  return (
    <PaneFrame>
      <TabsRoot
        value={tab}
        onValueChange={(v) => setTab(v as PreviewTab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <article className={cardClass}>
          <header className={headerClass}>
            <div className="flex items-start justify-between gap-4">
              <CardTitle
                title={draft.title}
                subtitle={draft.subtitle}
                {...(editMode ? { badge: "Editing existing report" } : {})}
              />
              <SegmentedTabs />
            </div>
          </header>

          <TabsContent value="preview" className="min-h-0 flex-1 overflow-y-auto p-3">
            {html ? (
              <div className="overflow-hidden rounded-md border border-line">
                <ReportFrame html={html} />
              </div>
            ) : (
              <RenderedTable columns={draft.columns} rows={draft.placeholderRows} />
            )}
          </TabsContent>

          <TabsContent value="sql" className="flex min-h-0 flex-1 flex-col">
            <DataSourcesEditor
              blocks={editSteps}
              readOnly={!savedKey || !updateMutation}
              onAdd={addStep}
              onPatch={patchStep}
              onMove={moveStep}
              onRemove={removeStep}
            />
          </TabsContent>

          <TabsContent value="template" className="flex min-h-0 flex-1 flex-col">
            <TemplatePanel
              draft={draft}
              {...(savedKey ? { savedKey } : {})}
              {...(savedKey && updateTemplateMutation
                ? { onLoaded: handleTemplateLoaded, editTpl, onChangeTpl: setEditTpl }
                : {})}
            />
          </TabsContent>

          <PreviewFooter
            tab={tab}
            savedKey={savedKey}
            draft={draft}
            sqlDirty={stepsDirty}
            tplDirty={tplDirty}
            saveStatus={saveMutation?.status ?? "idle"}
            sqlStatus={updateMutation?.status ?? "idle"}
            tplStatus={updateTemplateMutation?.status ?? "idle"}
            onSaveDraft={() => {
              if (draft && !savedKey && saveMutation) {
                // Only forward the stepper's steps if the user actually
                // touched them beyond the AI's single-SQL default. Keeps
                // the wire payload small and lets the server flatten.
                const dirtyStepper = !stepsEqual(editSteps, derivedSteps);
                saveMutation.mutate({
                  draft,
                  ...(dirtyStepper ? { dataSources: normalizeSteps(editSteps) } : {}),
                });
              }
            }}
            onSaveSql={() => {
              if (savedKey && updateMutation) {
                const normalized = normalizeSteps(editSteps);
                // Server needs `sqlTemplate` — derive it from step 0 so
                // legacy edit flows keep working even when the user has
                // added extra steps (only step 0's script matters for
                // the auto template + preview).
                const primary = normalized[0]?.script ?? "";
                updateMutation.mutate({
                  reportKey: savedKey,
                  sqlTemplate: primary,
                  dataSources: normalized,
                });
              }
            }}
            onSaveTpl={() => {
              if (savedKey && updateTemplateMutation) {
                updateTemplateMutation.mutate({
                  reportKey: savedKey,
                  displayTemplate: editTpl,
                });
              }
            }}
            onDiscardSql={() => setEditSteps(savedSteps)}
            onDiscardTpl={() => setEditTpl(savedTpl)}
          />
        </article>
      </TabsRoot>
    </PaneFrame>
  );
}

interface PreviewFooterProps {
  tab: PreviewTab;
  savedKey: string | undefined;
  draft: DraftDefinition | null;
  sqlDirty: boolean;
  tplDirty: boolean;
  saveStatus: "idle" | "pending" | "success" | "error";
  sqlStatus: "idle" | "pending" | "success" | "error";
  tplStatus: "idle" | "pending" | "success" | "error";
  onSaveDraft: () => void;
  onSaveSql: () => void;
  onSaveTpl: () => void;
  onDiscardSql: () => void;
  onDiscardTpl: () => void;
}

function PreviewFooter({
  tab,
  savedKey,
  draft,
  sqlDirty,
  tplDirty,
  saveStatus,
  sqlStatus,
  tplStatus,
  onSaveDraft,
  onSaveSql,
  onSaveTpl,
  onDiscardSql,
  onDiscardTpl,
}: PreviewFooterProps) {
  // Three modes:
  //   - Unsaved draft ⇒ Save persists the whole draft (first save).
  //     Discard is inert — there's nothing to revert against yet.
  //   - Saved, editable tab ⇒ Save/Discard drive the active tab's
  //     dirty edits.
  //   - Saved, non-editable tab ⇒ both inert (no editable state).
  const firstSave = !savedKey && draft !== null;
  const editableTab = tab === "sql" || tab === "template";
  const dirty = tab === "sql" ? sqlDirty : tab === "template" ? tplDirty : false;
  const status = firstSave
    ? saveStatus
    : tab === "sql"
      ? sqlStatus
      : tab === "template"
        ? tplStatus
        : "idle";
  const onSave = firstSave
    ? onSaveDraft
    : tab === "sql"
      ? onSaveSql
      : tab === "template"
        ? onSaveTpl
        : undefined;
  const onDiscard =
    tab === "sql" ? onDiscardSql : tab === "template" ? onDiscardTpl : undefined;
  const saveLabel = tab === "template" && !firstSave ? "Save template" : "Save changes";
  const busy = status === "pending";
  const armedSave = firstSave
    ? !busy
    : Boolean(savedKey) && editableTab && dirty && !busy;
  const armedDiscard = Boolean(savedKey) && editableTab && dirty && !busy;

  return (
    <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-line bg-paper px-4 py-2.5">
      <button
        type="button"
        onClick={armedDiscard && onDiscard ? onDiscard : undefined}
        disabled={!armedDiscard}
        className="rounded-md px-3 py-1.5 text-sm text-muted transition-colors hover:bg-bg hover:text-ink disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
      >
        Discard
      </button>
      <button
        type="button"
        onClick={armedSave && onSave ? onSave : undefined}
        disabled={!armedSave}
        className="rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-sky-500 disabled:cursor-default disabled:opacity-40"
      >
        {busy ? "Saving…" : saveLabel}
      </button>
    </footer>
  );
}

/** Always-visible footer shown in the pending/empty branches. Same silhouette,
 * both buttons inert — the affordance stays put across every pane state. */
function IdlePreviewFooter() {
  return (
    <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-line bg-paper px-4 py-2.5">
      <button
        type="button"
        disabled
        className="rounded-md px-3 py-1.5 text-sm text-muted opacity-40 disabled:cursor-default"
      >
        Discard
      </button>
      <button
        type="button"
        disabled
        className="rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white opacity-40 disabled:cursor-default"
      >
        Save changes
      </button>
    </footer>
  );
}

// ── Card chrome ─────────────────────────────────────────────────────

const cardClass =
  "flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl border border-line bg-paper shadow-sm";

const headerClass = "shrink-0 border-b border-line px-4 py-3";

/**
 * The slate gutter around the white report card. Owns the outer
 * padding so every pane state (empty / building / draft) sits inside
 * the same card silhouette.
 */
function PaneFrame({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-0 flex-1 flex-col p-2 lg:p-3">{children}</div>;
}

function CardTitle({
  title,
  subtitle,
  badge,
  badgeTone = "edit",
}: {
  title: string;
  subtitle?: string | undefined;
  badge?: string;
  badgeTone?: "edit" | "progress";
}) {
  return (
    <div className="min-w-0">
      {badge ? (
        <p
          className={cn(
            "mb-1 inline-flex items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-xs font-medium uppercase tracking-wider",
            badgeTone === "progress" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700",
          )}
        >
          {badgeTone === "progress" ? (
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-500" aria-hidden />
          ) : null}
          {badge}
        </p>
      ) : null}
      <h2 className="truncate text-base font-semibold tracking-tight text-ink">{title}</h2>
      {subtitle ? <p className="mt-0.5 truncate text-sm text-muted">{subtitle}</p> : null}
    </div>
  );
}

/**
 * Segmented switch in the card header — overrides the underline tab
 * primitives into a pill group (slate track, white active thumb).
 */
function SegmentedTabs() {
  const trigger =
    "h-7 rounded-md border-b-0 -mb-0 px-3 text-xs font-medium text-muted " +
    "data-[state=active]:border-transparent data-[state=active]:bg-paper " +
    "data-[state=active]:text-ink data-[state=active]:shadow-sm data-[state=active]:font-semibold";
  return (
    <TabsList
      aria-label="Report inspection views"
      className="mx-0 shrink-0 gap-0.5 overflow-visible rounded-lg border-b-0 bg-slate-100 p-0.5 px-0.5"
    >
      <TabsTrigger value="preview" className={trigger}>
        Preview
      </TabsTrigger>
      <TabsTrigger value="sql" className={trigger}>
        SQL
      </TabsTrigger>
      <TabsTrigger value="template" className={trigger}>
        Template
      </TabsTrigger>
    </TabsList>
  );
}

// ── Preview bodies ──────────────────────────────────────────────────

function EmptyBody() {
  return (
    <div className="flex h-full items-center justify-center py-6">
      <div className="max-w-sm text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-sky-50 text-sky-600">
          <TableProperties className="h-7 w-7" strokeWidth={1.75} aria-hidden />
        </div>
        <h3 className="mt-4 text-base font-semibold text-ink">Your report will render here</h3>
        <p className="mt-1.5 text-sm text-muted">
          Ask Flint for a report on the left. The preview runs read-only against your workspace and
          appears here.
        </p>
      </div>
    </div>
  );
}

/**
 * Building state — shown while the agent is still streaming the
 * report definition. Real column headers appear one by one as they
 * arrive; shimmer rows stand in for the data until the SQL runs.
 */
function BuildingBody({ columns }: { columns: string[] }) {
  const colCount = Math.max(columns.length, 3);
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {Array.from({ length: colCount }, (_, i) => (
              <th
                key={i}
                scope="col"
                className="whitespace-nowrap border-b border-line px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted"
              >
                {columns[i] ?? (
                  <span className="inline-block h-3 w-16 animate-pulse rounded-sm bg-slate-200 align-middle" />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }, (_, rowIdx) => (
            <tr key={rowIdx} className="odd:bg-paper even:bg-raised/40">
              {Array.from({ length: colCount }, (_, colIdx) => (
                <td key={colIdx} className="border-b border-line/60 px-3 py-2.5">
                  <span
                    className={cn(
                      "block h-3 animate-pulse rounded-sm bg-slate-200/80",
                      colIdx === 0 ? "w-28" : "w-16",
                    )}
                    style={{ animationDelay: `${(rowIdx * colCount + colIdx) * 60}ms` }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RenderedTable({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={`${col}-${i}`}
                scope="col"
                className="whitespace-nowrap border-b border-line px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={Math.max(columns.length, 1)}
                className="px-3 py-6 text-center text-xs italic text-muted"
              >
                No data available
              </td>
            </tr>
          ) : (
            rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="odd:bg-paper even:bg-raised/40">
                {row.map((cell, colIdx) => (
                  <td
                    key={colIdx}
                    className={cn(
                      "whitespace-nowrap border-b border-line/60 px-3 py-2",
                      colIdx === 0 ? "font-medium text-ink" : "text-ink-2",
                    )}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Data-source pipeline helpers ────────────────────────────────────

/**
 * Seed the SQL tab's stepper from the current draft.
 *
 *  - Draft carries `dataSources` (edit-mode hydration path or a
 *    server-persisted snapshot) → return them ordered by `order`.
 *  - Otherwise (fresh AI draft) → wrap `sqlTemplate` into one implicit
 *    step keyed `rows`. Matches what `saveAiDraft` writes when it
 *    flattens a single-SQL draft server-side.
 *  - No draft at all → return an empty array; the pane shouldn't be
 *    rendering the SQL tab in that state anyway.
 */
function deriveSteps(draft: DraftDefinition | null): DataSource[] {
  if (!draft) return [];
  if (draft.dataSources && draft.dataSources.length > 0) {
    return [...draft.dataSources].sort((a, b) => a.order - b.order);
  }
  return [
    {
      key: "rows",
      name: "Rows",
      type: "sql",
      order: 0,
      script: draft.sqlTemplate,
    },
  ];
}

/** Shallow structural equality — good enough for the footer's dirty check. */
function stepsEqual(a: DataSource[], b: DataSource[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (
      x.key !== y.key ||
      x.name !== y.name ||
      x.type !== y.type ||
      x.order !== y.order ||
      x.script !== y.script
    ) {
      return false;
    }
  }
  return true;
}

/** Re-index `order` to match array position — belt-and-suspenders for
 *  the wire payload; the domain re-normalises again on write. */
function normalizeSteps(steps: DataSource[]): DataSource[] {
  return steps.map((s, i) => ({ ...s, order: i }));
}

// ── Code editor shell (SQL / Template) ──────────────────────────────

/**
 * Full-height Monaco surface. `onChange` present → editable; absent →
 * read-only (still selectable for copy). The editor owns its scroll —
 * the card body never grows. No top toolbar — the tab bar above already
 * identifies which language the editor is on.
 */
function CodeEditor({
  label,
  value,
  onChange,
  footer,
}: {
  /** ARIA label for the editor + hint used to derive the Monaco language. */
  label: string;
  value: string;
  onChange?: (next: string) => void;
  /** One-line hint under the editor. */
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-hidden bg-[#0f172a]">
        <MonacoCodeEditor
          value={value}
          {...(onChange ? { onChange } : {})}
          language={toMonacoLanguage(label)}
          ariaLabel={label}
        />
      </div>
      {footer ? (
        <div className="shrink-0 border-t border-line bg-raised/50 px-4 py-2 text-xs text-muted">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

function toMonacoLanguage(display: string | undefined): "sql" | "html" | "json" {
  const s = (display ?? "").toLowerCase();
  if (s.startsWith("sql")) return "sql";
  if (s.startsWith("json")) return "json";
  // Nunjucks + fallback — HTML is the closest built-in mode; template tags
  // ({{ }}, {% %}) render as plain text on the HTML surface.
  return "html";
}

// SQL tab body — see `<DataSourcesEditor />` in ./data-sources-editor.tsx.
// The prior single-Monaco `SqlPanel` was retired when the manual
// `/reports/build` route folded into the chat's SQL tab as the stepper.

// ── Template panel ──────────────────────────────────────────────────

/**
 * The Nunjucks HTML the viewer renders. Source priority:
 *   1. Saved report → `getDefinition().displayTemplate` (editable).
 *   2. Unsaved draft WITH a custom agent-authored template → show it.
 *   3. Unsaved plain draft → `previewAutoTemplate` (the exact Nunjucks
 *      that will be baked in on save).
 */
function TemplatePanel({
  draft,
  savedKey,
  onLoaded,
  editTpl,
  onChangeTpl,
}: {
  draft: DraftDefinition;
  savedKey?: string;
  onLoaded?: (source: string) => void;
  editTpl?: string;
  onChangeTpl?: (next: string) => void;
}) {
  const savedQuery = trpc.reports.getDefinition.useQuery(
    { key: savedKey ?? "" },
    { enabled: !!savedKey },
  );
  const previewQuery = trpc.reports.previewAutoTemplate.useQuery(
    {
      sqlTemplate: draft.sqlTemplate,
      labels: draft.columns,
      reportName: draft.title,
    },
    { enabled: !savedKey && !draft.displayTemplate },
  );

  const template = savedKey
    ? (savedQuery.data?.displayTemplate ?? null)
    : (draft.displayTemplate ?? previewQuery.data?.template ?? null);
  const loading = savedKey
    ? savedQuery.isLoading
    : !draft.displayTemplate && previewQuery.isLoading;
  const loadError = savedKey ? savedQuery.error : previewQuery.error;

  // When the saved template first arrives, hydrate the parent-owned
  // edit/saved watermarks so the footer can drive Save/Discard.
  const hasSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onLoaded) return;
    if (template === null) return;
    if (hasSeededRef.current === template) return;
    hasSeededRef.current = template;
    onLoaded(template);
  }, [template, onLoaded]);

  if (loading) return <PanelNotice>Loading template…</PanelNotice>;
  if (loadError) {
    return <PanelNotice tone="error">Couldn't build template: {loadError.message}</PanelNotice>;
  }

  const editable = Boolean(savedKey && onChangeTpl && editTpl !== undefined);

  return (
    <CodeEditor
      label="template.njk"
      value={editable ? (editTpl ?? "") : (template ?? "— (no template stored)")}
      {...(editable && onChangeTpl ? { onChange: onChangeTpl } : {})}
      footer={
        savedKey ? (
          <>
            Saves overwrite the template on <code className="font-mono">/reports/{savedKey}</code>.
            Editing SQL regenerates this template and overwrites your changes.
          </>
        ) : draft.displayTemplate ? (
          "Custom layout authored by Flint AI — saved with the report as-is."
        ) : (
          "Preview — this is exactly what will be saved with the report."
        )
      }
    />
  );
}

function PanelNotice({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "error";
}) {
  return (
    <div className="p-5">
      <p className={cn("text-sm", tone === "error" ? "text-red-700" : "text-muted")}>{children}</p>
    </div>
  );
}

